import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { http } from "../../utils/axios";
import { setCredentials, setLoading } from "../../store/slices/authSlice";
import { getBusinessDetails } from "../../store/slices/businessOwnerSlice";
import { showApiError, showApiSuccess } from "../../utils/toast";
import { Eye, EyeOff } from "lucide-react";
import usePremiumStatus from "../../hooks/usePremiumStatus";

export default function BusinessLoginPage() {
  const logo = new URL(`../../assets/images/logo.png`, import.meta.url);

  const [mobile, setMobile] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { fetchSubscriptionData } = usePremiumStatus();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    dispatch(setLoading(true));

    try {
      const res = await http.post("/dashboard/auth/business/login", {
        mobile,
        password,
      });

      const { data } = res;
      const { token, user, is_reset_password_after_login } = data;

      // Set role from API response (normalize to lowercase for consistency)
      const userWithRole = {
        ...user,
        role: user?.role?.name?.toLowerCase() || "business",
        is_reset_password_after_login
      };

      // Always set credentials on successful login
      dispatch(setCredentials({ token, user: userWithRole }));

      // Fetch business details and subscription data after successful login
      try {
        const businessDetailsResult = await dispatch(getBusinessDetails());
        if (businessDetailsResult.type === "businessOwner/getBusinessDetails/fulfilled") {
          const businessData = businessDetailsResult.payload?.data;
          if (businessData?.uuid) {
            // Update user with business_id
            const userWithBusinessId = {
              ...userWithRole,
              business_id: businessData.uuid,
              business_name: businessData.business_name
            };
            dispatch(setCredentials({ token, user: userWithBusinessId }));
          }
        }

        // Fetch subscription data after setting credentials
        await fetchSubscriptionData(true); // Force fresh fetch on login

      } catch (businessErr) {
        console.warn("Failed to fetch business details or subscription:", businessErr);
        // Continue with login even if business details fetch fails
      }

      // Check if password reset is required after login
      if (is_reset_password_after_login) {
        // User needs to change password - redirect to first-time change password page
        showApiSuccess("Please update your password");
        navigate("/business/first-change-password");
      } else {
        // User can proceed directly to dashboard
        showApiSuccess("Login successful - Business!");
        navigate("/business/dashboard");
      }
    } catch (err) {
      showApiError(err, "Login failed. Please check credentials.");
    } finally {
      setIsSubmitting(false);
      dispatch(setLoading(false));
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100 p-4">
      <div className="w-full max-w-md bg-white rounded-lg shadow p-6">
        <div className="w-full justify-center flex-col flex items-center">
          <img src={logo} alt="Logo" className="w-48" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2 text-center">
            Business Login
          </h1>
          <p className="text-gray-600 text-center mb-6">
            Enter your credentials to access your account
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Mobile Number
            </label>
            <input
              type="tel"
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={mobile}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, ''); // Remove non-digits
                if (value.length <= 10) {
                  setMobile(value);
                }
              }}
              onKeyPress={(e) => {
                // Only allow digits
                if (!/[0-9]/.test(e.key) && e.key !== 'Backspace' && e.key !== 'Delete' && e.key !== 'Tab') {
                  e.preventDefault();
                }
              }}
              placeholder="9876543210"
              minLength={10}
              maxLength={10}
              pattern="[0-9]{10}"
              inputMode="numeric"
              required
            />
            {mobile.length > 0 && mobile.length < 10 && (
              <p className="text-red-500 text-xs mt-1">Mobile number must be exactly 10 digits</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Password
            </label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2"
              >
                {showPassword ? (
                  <EyeOff className="h-5 w-5 text-gray-500" />
                ) : (
                  <Eye className="h-5 w-5 text-gray-500" />
                )}
              </button>
            </div>
          </div>
          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-blue-600 text-white rounded-md px-4 py-2 hover:bg-blue-700 transition-colors disabled:bg-blue-400 flex items-center justify-center"
          >
            {isSubmitting ? (
              <>
                <svg
                  className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                Processing...
              </>
            ) : (
              "Login"
            )}
          </button>
        </form>

        <div className="text-center mt-4">
          <button
            type="button"
            onClick={() => navigate("/forgot-password")}
            className="text-blue-600 hover:text-blue-700 text-sm"
          >
            Forgot Password?
          </button>
        </div>
      </div>
    </div>
  );
}
